import clamp from "lodash/clamp";

import { PIP_SIZE_RATIO_BY_SIZE } from "../constants";
import type {
  VideoClipPipPosition,
  VideoClipPipShape,
  VideoClipPipSize,
} from "./useVideoClipSettingsStore";

const CAMERA_FOCAL_POINT_X = 0.5;
const CAMERA_FOCAL_POINT_Y = 0.42;

const DISPLAY_ASPECT_RATIO = 16 / 9;
const SQUARE_PIP_RADIUS_RATIO = 0.08;

const getVisibleRectForCoverDisplay = (
  width: number,
  height: number,
  displayAspectRatio: number,
) => {
  const sourceAspectRatio = width / height;

  // object-cover on a 16:9 container crops either horizontal or vertical edges.
  // Keep PiP inside the visible source rect so it doesn't appear clipped.
  if (sourceAspectRatio > displayAspectRatio) {
    const visibleWidth = height * displayAspectRatio;
    const x = (width - visibleWidth) / 2;
    return { x, y: 0, width: visibleWidth, height };
  }

  if (sourceAspectRatio < displayAspectRatio) {
    const visibleHeight = width / displayAspectRatio;
    const y = (height - visibleHeight) / 2;
    return { x: 0, y, width, height: visibleHeight };
  }

  return { x: 0, y: 0, width, height };
};

const addRoundedRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const clampedRadius = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, clampedRadius);
};

const getSourceRectForCoverSquare = (
  sourceWidth: number,
  sourceHeight: number,
) => {
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return null;
  }

  const sourceAspectRatio = sourceWidth / sourceHeight;

  if (sourceAspectRatio > 1) {
    const cropWidth = sourceHeight;
    const maxX = sourceWidth - cropWidth;
    const x = clamp((sourceWidth - cropWidth) * CAMERA_FOCAL_POINT_X, 0, maxX);
    return { x, y: 0, width: cropWidth, height: sourceHeight };
  }

  const cropHeight = sourceWidth;
  const maxY = sourceHeight - cropHeight;
  const y = clamp((sourceHeight - cropHeight) * CAMERA_FOCAL_POINT_Y, 0, maxY);
  return { x: 0, y, width: sourceWidth, height: cropHeight };
};

interface PipGeometry {
  pipDiameter: number;
  pipX: number;
  pipY: number;
  radius: number;
  roundedSquareRadius: number;
}

const getPipGeometry = (
  canvas: HTMLCanvasElement,
  pipPosition: VideoClipPipPosition,
  pipDiameter: number,
): PipGeometry => {
  // Equivalent to Tailwind's `p-4` (16px) scaled to canvas resolution
  const margin = Math.round(canvas.height * 0.044);
  const visibleRect = getVisibleRectForCoverDisplay(
    canvas.width,
    canvas.height,
    DISPLAY_ASPECT_RATIO,
  );
  const minX = visibleRect.x + margin;
  const maxX = Math.max(
    minX,
    visibleRect.x + visibleRect.width - pipDiameter - margin,
  );
  const minY = visibleRect.y + margin;
  const maxY = Math.max(
    minY,
    visibleRect.y + visibleRect.height - pipDiameter - margin,
  );
  return {
    pipDiameter,
    pipX: pipPosition.endsWith("right") ? maxX : minX,
    pipY: pipPosition.startsWith("bottom") ? maxY : minY,
    radius: pipDiameter / 2,
    roundedSquareRadius: Math.max(
      8,
      Math.round(pipDiameter * SQUARE_PIP_RADIUS_RATIO),
    ),
  };
};

const addPipShapePath = (
  ctx: CanvasRenderingContext2D,
  geometry: PipGeometry,
  pipShape: VideoClipPipShape,
) => {
  const { pipX, pipY, pipDiameter, radius, roundedSquareRadius } = geometry;
  if (pipShape === "round") {
    ctx.beginPath();
    ctx.arc(pipX + radius, pipY + radius, radius, 0, Math.PI * 2);
    ctx.closePath();
  } else {
    addRoundedRectPath(
      ctx,
      pipX,
      pipY,
      pipDiameter,
      pipDiameter,
      roundedSquareRadius,
    );
  }
};

const drawPipImage = (
  ctx: CanvasRenderingContext2D,
  cameraVideo: HTMLVideoElement,
  geometry: PipGeometry,
  pipShape: VideoClipPipShape,
) => {
  const { pipX, pipY, pipDiameter } = geometry;
  ctx.save();
  addPipShapePath(ctx, geometry, pipShape);
  ctx.clip();
  const sourceRect = getSourceRectForCoverSquare(
    cameraVideo.videoWidth,
    cameraVideo.videoHeight,
  );
  if (sourceRect) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
      cameraVideo,
      sourceRect.x,
      sourceRect.y,
      sourceRect.width,
      sourceRect.height,
      pipX,
      pipY,
      pipDiameter,
      pipDiameter,
    );
  }
  ctx.restore();
};

const drawPipBorder = (
  ctx: CanvasRenderingContext2D,
  geometry: PipGeometry,
  pipShape: VideoClipPipShape,
) => {
  ctx.save();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
  ctx.lineWidth = Math.max(2, Math.round(geometry.pipDiameter * 0.02));
  addPipShapePath(ctx, geometry, pipShape);
  ctx.stroke();
  ctx.restore();
};

export const drawPipOverlay = (
  ctx: CanvasRenderingContext2D,
  cameraVideo: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  pipPosition: VideoClipPipPosition,
  pipShape: VideoClipPipShape,
  pipSize: VideoClipPipSize,
) => {
  const pipDiameter = Math.round(
    Math.min(canvas.width, canvas.height) * PIP_SIZE_RATIO_BY_SIZE[pipSize],
  );
  const geometry = getPipGeometry(canvas, pipPosition, pipDiameter);
  drawPipImage(ctx, cameraVideo, geometry, pipShape);
  drawPipBorder(ctx, geometry, pipShape);
};
